import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { ordersAPI, adminAPI } from '../services/api.ts';
import { Order } from '../types/index.ts';
import LoadingSpinner from '../components/LoadingSpinner.tsx';
import H2GOLogo from '../components/H2GOLogo.tsx';
import OrderStatusHistory from '../components/OrderStatusHistory.tsx';
import ErrorBanner from '../components/ErrorBanner.tsx';
import { Z_INDEX_CLASSES } from '../constants/zIndex.ts';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);
  const [bannerType, setBannerType] = useState<'error' | 'warning' | 'success' | 'info'>('error');
  
  // Admin state
  const [showOrderManagement, setShowOrderManagement] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newPaymentStatus, setNewPaymentStatus] = useState('');
  const [newPaymentMethod, setNewPaymentMethod] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [amountDue, setAmountDue] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Status history modal state
  const [showStatusHistory, setShowStatusHistory] = useState(false);

  const fetchOrder = useCallback(async (orderId?: string) => {
    if (!orderId) return;
    
    try {
      setLoading(true);
      // Use admin API if user is admin, otherwise use customer API
      const response = isAdmin 
        ? await adminAPI.getOrderDetails(parseInt(orderId))
        : await ordersAPI.getOrder(parseInt(orderId));
      setOrder(response.data.data.order);
    } catch (error: any) {
      console.error('Error fetching order:', error);
      setBannerMessage('Failed to load order details. Please try again.');
      setBannerType('error');
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (id) {
      fetchOrder(id);
    }
  }, [isAuthenticated, navigate, id, fetchOrder]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'preparing': return 'bg-orange-100 text-orange-800';
      case 'out_for_delivery': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: string | number) => {
    return `₹${parseFloat(amount.toString()).toFixed(2)}`;
  };

  const getStatusProgress = (status: string) => {
    const statuses = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered'];
    const currentIndex = statuses.indexOf(status);
    return currentIndex >= 0 ? currentIndex + 1 : 0;
  };

  // Admin functions
  const handleOrderUpdate = async () => {
    if (!order) return;
    
    const hasStatusChange = newStatus && newStatus !== order.status;
    const hasPaymentChange = newPaymentStatus && newPaymentStatus !== order.paymentStatus;
    
    if (!hasStatusChange && !hasPaymentChange) {
      setBannerMessage('Please make at least one change to update the order.');
      setBannerType('warning');
      return;
    }

    // Validate status progression (unidirectional)
    if (hasStatusChange) {
      const statusValidationMessage = getStatusValidationMessage(order.status, newStatus);
      if (statusValidationMessage) {
        setBannerMessage(statusValidationMessage);
        setBannerType('error');
        return;
      }
    }

    // Validate payment requirement for confirmation
    if (hasStatusChange && newStatus === 'confirmed') {
      const effectivePaymentStatus = hasPaymentChange ? newPaymentStatus : order.paymentStatus;
      const paymentValidationMessage = getPaymentValidationMessage(newStatus, effectivePaymentStatus);
      if (paymentValidationMessage) {
        setBannerMessage(paymentValidationMessage);
        setBannerType('error');
        return;
      }
    }
    
    try {
      setUpdating(true);
      
      const promises = [];
      
      // Update order status if changed
      if (hasStatusChange) {
        promises.push(
          adminAPI.updateOrderStatus(order.id, {
            status: newStatus,
            adminNotes: adminNotes.trim() || undefined,
            changeReason: changeReason.trim() || undefined
          })
        );
      }
      
      // Update payment status if changed
      if (hasPaymentChange) {
        promises.push(
          adminAPI.updateOrderPaymentStatus(order.id, {
            paymentStatus: newPaymentStatus,
            paymentMethod: newPaymentMethod || undefined,
            amountPaid: amountPaid ? parseFloat(amountPaid) : undefined,
            amountDue: amountDue ? parseFloat(amountDue) : undefined,
            adminNotes: adminNotes.trim() || undefined,
            changeReason: changeReason.trim() || undefined
          })
        );
      }
      
      // Execute all updates
      await Promise.all(promises);
      
      // Refresh order data
      await fetchOrder(id);
      
      // Reset form
      setShowOrderManagement(false);
      setNewStatus('');
      setNewPaymentStatus('');
      setNewPaymentMethod('');
      setAmountPaid('');
      setAmountDue('');
      setAdminNotes('');
      setChangeReason('');
      
      // Show success banner
      const changes = [];
      if (hasStatusChange) changes.push('order status');
      if (hasPaymentChange) changes.push('payment status');
      setBannerMessage(`${changes.join(' and ')} updated successfully!`);
      setBannerType('success');
      
    } catch (error: any) {
      console.error('Error updating order:', error);
      
      // Handle specific error types with user-friendly messages
      const errorResponse = error.response?.data;
      let errorMessage = 'Failed to update order';
      
      if (errorResponse) {
        switch (errorResponse.errorType) {
          case 'INSUFFICIENT_STOCK':
            errorMessage = `❌ Stock Issue: ${errorResponse.message}`;
            break;
          case 'ORDER_NOT_FOUND':
            errorMessage = '❌ Order not found. Please refresh the page.';
            break;
          case 'INVALID_STATUS':
            errorMessage = '❌ Invalid status selected. Please try again.';
            break;
          case 'INVALID_PAYMENT_STATUS':
            errorMessage = '❌ Invalid payment status selected. Please try again.';
            break;
          case 'STATUS_ALREADY_SET':
            errorMessage = 'ℹ️ Order status is already set to this value.';
            break;
          case 'PAYMENT_STATUS_ALREADY_SET':
            errorMessage = 'ℹ️ Payment status is already set to this value.';
            break;
          case 'VALIDATION_ERROR':
            errorMessage = `❌ Validation Error: ${errorResponse.message}`;
            break;
          case 'DATABASE_ERROR':
            errorMessage = '❌ Database error. Please try again later.';
            break;
          default:
            errorMessage = errorResponse.message || errorMessage;
        }
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      setBannerMessage(errorMessage);
      setBannerType('error');
    } finally {
      setUpdating(false);
    }
  };

  const openOrderManagement = () => {
    if (order) {
      setError(null); // Clear any previous errors
      setBannerMessage(null); // Clear any previous banner messages
      setNewStatus(order.status);
      setNewPaymentStatus(order.paymentStatus);
      setNewPaymentMethod(order.paymentMethod || '');
      setAmountPaid(order.amountPaid?.toString() || '');
      setAmountDue(order.amountDue?.toString() || '');
      setAdminNotes(order.adminNotes || '');
      setShowOrderManagement(true);
    }
  };

  const openStatusHistory = () => {
    setShowStatusHistory(true);
  };

  const closeStatusHistory = () => {
    setShowStatusHistory(false);
  };

  const clearBanner = () => {
    setBannerMessage(null);
  };

  // Business logic validation functions
  const getStatusOrder = (status: string): number => {
    const statusOrder = {
      'pending': 0,
      'confirmed': 1,
      'preparing': 2,
      'out_for_delivery': 3,
      'delivered': 4,
      'cancelled': -1 // Cancelled can be set from any status
    };
    return statusOrder[status as keyof typeof statusOrder] || 0;
  };

  const canUpdateToStatus = (currentStatus: string, newStatus: string): boolean => {
    const currentOrder = getStatusOrder(currentStatus);
    const newOrder = getStatusOrder(newStatus);
    
    // Can always cancel an order
    if (newStatus === 'cancelled') return true;
    
    // Can't go backwards in status (except to cancelled)
    if (newOrder < currentOrder) return false;
    
    // Can only move forward one step at a time (except to cancelled)
    if (newOrder > currentOrder + 1) return false;
    
    return true;
  };

  const canConfirmOrder = (paymentStatus: string): boolean => {
    return paymentStatus === 'paid';
  };

  const getStatusValidationMessage = (currentStatus: string, newStatus: string): string | null => {
    if (!canUpdateToStatus(currentStatus, newStatus)) {
      if (newStatus === 'cancelled') {
        return null; // Can always cancel
      }
      return `Cannot change status from "${currentStatus}" to "${newStatus}". Order status can only move forward.`;
    }
    return null;
  };

  const getPaymentValidationMessage = (newStatus: string, paymentStatus: string): string | null => {
    if (newStatus === 'confirmed' && !canConfirmOrder(paymentStatus)) {
      return 'Order cannot be confirmed until payment is completed. Please update payment status to "Paid" first.';
    }
    return null;
  };


  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <LoadingSpinner size="lg" text="Loading order details..." />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto mb-6">
              <div className="flex items-center justify-center mb-3">
                <svg className="w-8 h-8 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-semibold text-red-800">Error</h3>
              </div>
              <p className="text-red-700 text-sm">{error || 'Order not found'}</p>
            </div>
            <button
              onClick={() => navigate('/orders')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Floating Error Banner */}
      <ErrorBanner 
        message={bannerMessage}
        type={bannerType}
        duration={bannerType === 'success' ? 3000 : 0} // Auto-hide success messages
        onClose={clearBanner}
      />
      
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Link to="/orders" className="hover:text-gray-700">Orders</Link>
              <span>/</span>
              <span className="text-gray-900">Order #{order.orderNumber}</span>
            </div>
          </nav>

          {/* Order Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="mb-4 lg:mb-0">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Order #{order.orderNumber}
                  </h1>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Placed on {formatDate(order.createdAt)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <div className="text-xs font-medium text-gray-500 mb-2">Order Status</div>
                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}>
                      {order.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-medium text-gray-500 mb-2">Payment Status</div>
                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${getPaymentStatusColor(order.paymentStatus)}`}>
                      {order.paymentStatus.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Progress */}
            <div className="px-6 py-6 relative z-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Order Progress
                {/* Debug info - remove in production */}
                {process.env.NODE_ENV === 'development' && (
                  <span className="text-xs text-gray-500 ml-2">(Z-Index: z-0)</span>
                )}
              </h3>
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-200">
                  <div 
                    className="h-full bg-gradient-to-r from-yellow-500 to-green-500 transition-all duration-500"
                    style={{ width: `${(getStatusProgress(order.status) / 4) * 100}%` }}
                  ></div>
                </div>
                
                {/* Progress Steps */}
                <div className="relative flex justify-between">
                  {['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered'].map((status, index) => {
                    const isActive = index <= getStatusProgress(order.status);
                    const isCurrent = order.status === status;
                    const statusLabels = {
                      pending: 'Order Placed',
                      confirmed: 'Confirmed',
                      preparing: 'Preparing',
                      out_for_delivery: 'Out for Delivery',
                      delivered: 'Delivered'
                    };
                    
                    return (
                      <div key={status} className="flex flex-col items-center relative z-0">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold shadow-lg transition-all duration-300 ${
                          isActive 
                            ? isCurrent 
                              ? 'bg-yellow-500 text-white ring-4 ring-yellow-200' 
                              : 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}>
                          {isActive ? (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            index + 1
                          )}
                        </div>
                        <div className="mt-3 text-center">
                          <div className={`text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                            {statusLabels[status as keyof typeof statusLabels]}
                          </div>
                          {isCurrent && (
                            <div className="text-xs text-gray-500 mt-1">Current Status</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Admin Controls */}
          {isAdmin && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Admin Controls
                </h2>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={openOrderManagement}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Manage Order
                  </button>
                  
                  <button
                    onClick={openStatusHistory}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    View History
                  </button>
                  
                  {/* Test Banner Button - Remove in production */}
                  {process.env.NODE_ENV === 'development' && (
                    <button
                      onClick={() => {
                        setBannerMessage('This is a test error message to demonstrate the floating banner functionality!');
                        setBannerType('error');
                      }}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Test Banner
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Order Items */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    Order Items
                  </h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {order.orderItems.map((item, index) => (
                      <div key={item.id} className={`flex items-center space-x-4 p-4 rounded-lg transition-all duration-200 hover:shadow-md ${
                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                      } border border-gray-100`}>
                        <div className="w-20 h-20 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
                          {item.product.image ? (
                            <img
                              src={item.product.image}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = `https://via.placeholder.com/80x80/f3f4f6/9ca3af?text=${encodeURIComponent(item.product.name)}`;
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
                              <H2GOLogo size="md" showText={false} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-lg">{item.product.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{item.product.description}</p>
                          <div className="flex items-center space-x-6 mt-3">
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                              {item.product.size}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 12h6m-6 4h6" />
                              </svg>
                              Qty: {item.quantity}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                              {formatCurrency(item.unitPrice)} each
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-lg text-gray-900">
                            {formatCurrency(item.totalPrice)}
                          </div>
                          {item.depositAmount > 0 && (
                            <div className="text-sm text-gray-600 mt-1">
                              + {formatCurrency(item.depositAmount)} deposit
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Delivery Information
                  </h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Delivery Address
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-gray-700 space-y-2">
                          <p className="font-medium">{order.deliveryAddress}</p>
                          <p className="text-gray-600">{order.deliveryCity} - {order.deliveryPincode}</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Contact & Timing
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        <div className="flex items-center text-gray-700">
                          <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {order.deliveryPhone}
                        </div>
                        {order.deliveryDate && (
                          <div className="flex items-center text-gray-700">
                            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {new Date(order.deliveryDate).toLocaleDateString('en-IN', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                        )}
                        {order.deliveryTime && (
                          <div className="flex items-center text-gray-700">
                            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {order.deliveryTime}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {order.notes && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Special Instructions
                      </h3>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-gray-700 italic">"{order.notes}"</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-8">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Order Summary
                  </h2>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(order.totalAmount - order.deliveryCharge)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Delivery Charge:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(order.deliveryCharge)}</span>
                    </div>
                    {order.depositAmount > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600">Deposit:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(order.depositAmount)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="flex justify-between items-center py-2">
                        <span className="font-semibold text-gray-900">Total Amount:</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(order.totalAmount)}</span>
                      </div>
                      {order.depositAmount > 0 && (
                        <div className="flex justify-between items-center py-2 mt-2 bg-blue-50 rounded-lg px-3 py-3">
                          <span className="font-bold text-lg text-blue-900">Grand Total:</span>
                          <span className="font-bold text-lg text-blue-900">{formatCurrency(order.totalAmount + order.depositAmount)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-4">Payment Details</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Method:</span>
                        <span className="font-medium text-gray-900">{order.paymentMethod?.toUpperCase() || 'CASH'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Status:</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPaymentStatusColor(order.paymentStatus)}`}>
                          {order.paymentStatus.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Admin Notes */}
                  {isAdmin && order.adminNotes && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Admin Notes
                      </h3>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-sm text-gray-700">{order.adminNotes}</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <Link
                      to="/orders"
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 text-center block font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Back to Orders
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Management Modal */}
      {showOrderManagement && (
        <div className={`fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center ${Z_INDEX_CLASSES.MODAL_OVERLAY} p-4`} style={{ zIndex: 60 }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 ease-out flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Order Management</h3>
                    <p className="text-blue-100 text-xs">Update order status and payment information</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowOrderManagement(false);
                    setError(null);
                    setBannerMessage(null);
                  }}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-6 overflow-y-auto">

              <div className="space-y-3">
                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-3 shadow-sm">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-4 h-4 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <div className="ml-2">
                        <h3 className="text-xs font-medium text-red-800">Error</h3>
                        <p className="text-red-700 text-xs mt-1">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Business Rules Info */}
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-start">
                    <div className="p-1.5 bg-amber-100 rounded-lg mr-2 flex-shrink-0">
                      <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="text-xs font-semibold text-amber-800 mb-1">Business Rules</h5>
                      <ul className="text-xs text-amber-700 space-y-0.5">
                        <li>• Order status can only move forward (unidirectional)</li>
                        <li>• Orders can only be confirmed if payment is completed</li>
                        <li>• Update payment status first if needed before confirming</li>
                        <li>• Orders can be cancelled from any status</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Order Status Section - Most Important */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="p-1.5 bg-blue-100 rounded-lg mr-2">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Order Status</h4>
                      <p className="text-gray-600 text-xs">Update the current status</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Current Status
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white shadow-sm"
                    >
                      <option value="pending">📋 Pending</option>
                      <option 
                        value="confirmed" 
                        disabled={order && !canUpdateToStatus(order.status, 'confirmed')}
                      >
                        ✅ Confirmed {order && !canUpdateToStatus(order.status, 'confirmed') ? '(Not Available)' : ''}
                      </option>
                      <option 
                        value="preparing" 
                        disabled={order && !canUpdateToStatus(order.status, 'preparing')}
                      >
                        👨‍🍳 Preparing {order && !canUpdateToStatus(order.status, 'preparing') ? '(Not Available)' : ''}
                      </option>
                      <option 
                        value="out_for_delivery" 
                        disabled={order && !canUpdateToStatus(order.status, 'out_for_delivery')}
                      >
                        🚚 Out for Delivery {order && !canUpdateToStatus(order.status, 'out_for_delivery') ? '(Not Available)' : ''}
                      </option>
                      <option 
                        value="delivered" 
                        disabled={order && !canUpdateToStatus(order.status, 'delivered')}
                      >
                        🎉 Delivered {order && !canUpdateToStatus(order.status, 'delivered') ? '(Not Available)' : ''}
                      </option>
                      <option value="cancelled">❌ Cancelled</option>
                    </select>
                    
                    {/* Status Validation Message */}
                    {order && newStatus && newStatus !== order.status && (
                      (() => {
                        const validationMessage = getStatusValidationMessage(order.status, newStatus);
                        const paymentMessage = getPaymentValidationMessage(newStatus, newPaymentStatus || order.paymentStatus);
                        const message = validationMessage || paymentMessage;
                        
                        if (message) {
                          return (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-start">
                                <svg className="w-5 h-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                                <p className="text-red-700 text-sm">{message}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()
                    )}
                  </div>
                </div>

                {/* Payment Status Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="p-1.5 bg-green-100 rounded-lg mr-2">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Payment Information</h4>
                      <p className="text-gray-600 text-xs">Manage payment status and amounts</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Payment Status
                        {order && newStatus === 'confirmed' && (newPaymentStatus || order.paymentStatus) !== 'paid' && (
                          <span className="ml-2 text-red-600 text-xs font-normal">(Required for confirmation)</span>
                        )}
                      </label>
                      <select
                        value={newPaymentStatus}
                        onChange={(e) => setNewPaymentStatus(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-colors bg-white shadow-sm ${
                          order && newStatus === 'confirmed' && (newPaymentStatus || order.paymentStatus) !== 'paid'
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-green-500'
                        }`}
                      >
                        <option value="pending">⏳ Pending</option>
                        <option value="paid">✅ Paid</option>
                        <option value="failed">❌ Failed</option>
                        <option value="refunded">🔄 Refunded</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Payment Method
                      </label>
                      <select
                        value={newPaymentMethod}
                        onChange={(e) => setNewPaymentMethod(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors bg-white shadow-sm"
                      >
                        <option value="">💳 Select Payment Method</option>
                        <option value="cash">💵 Cash</option>
                        <option value="card">💳 Card</option>
                        <option value="upi">📱 UPI</option>
                        <option value="netbanking">🏦 Net Banking</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Payment Amount Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        💰 Amount Paid
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-lg font-medium">₹</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={amountPaid}
                          onChange={(e) => setAmountPaid(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors bg-white shadow-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        📋 Amount Due
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-lg font-medium">₹</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={amountDue}
                          onChange={(e) => setAmountDue(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors bg-white shadow-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Summary */}
                  {order && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                      <div className="flex items-center mb-3">
                        <div className="p-1.5 bg-gray-200 rounded-lg mr-2">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h5 className="text-sm font-semibold text-gray-900">Order Summary</h5>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b border-gray-200">
                            <span className="text-gray-600 font-medium">Order Amount:</span>
                            <span className="font-semibold text-gray-900">₹{Number(order.totalAmount).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-200">
                            <span className="text-gray-600 font-medium">Deposit:</span>
                            <span className="font-semibold text-gray-900">₹{Number(order.depositAmount).toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b border-gray-200">
                            <span className="text-gray-600 font-medium">Delivery:</span>
                            <span className="font-semibold text-gray-900">₹{Number(order.deliveryCharge).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center py-3 bg-green-50 rounded-lg px-3">
                            <span className="text-green-800 font-bold text-lg">Grand Total:</span>
                            <span className="font-bold text-green-600 text-xl">₹{(Number(order.totalAmount) + Number(order.depositAmount) + Number(order.deliveryCharge)).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Additional Information Section */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center mb-3">
                    <div className="p-1.5 bg-purple-100 rounded-lg mr-2">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Additional Information</h4>
                      <p className="text-gray-600 text-xs">Add notes and reasons for changes</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        📝 Change Reason
                      </label>
                      <input
                        type="text"
                        value={changeReason}
                        onChange={(e) => setChangeReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors bg-white shadow-sm"
                        placeholder="Enter reason for changes..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        📋 Admin Notes
                      </label>
                      <textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors bg-white shadow-sm resize-none"
                        placeholder="Add internal notes for this order..."
                      />
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-6 border-t border-gray-200 shadow-lg">
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowOrderManagement(false);
                    setError(null);
                    setBannerMessage(null);
                  }}
                  className="flex-1 px-6 py-4 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium shadow-sm"
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleOrderUpdate}
                  disabled={updating || (order && newStatus && (
                    getStatusValidationMessage(order.status, newStatus) ||
                    getPaymentValidationMessage(newStatus, newPaymentStatus || order.paymentStatus)
                  ))}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold shadow-lg transform hover:scale-[1.02] disabled:transform-none"
                >
                  {updating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating Order...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Update Order
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Status History Modal */}
      {order && (
        <OrderStatusHistory
          orderId={order.id}
          isOpen={showStatusHistory}
          onClose={closeStatusHistory}
        />
      )}
    </div>
  );
};

export default OrderDetail;